import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const api = axios.create({
  baseURL: `${API_URL}/api`,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000, // 30 second timeout for large requests
});

// Add auth token to requests if available
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('auth_token') || localStorage.getItem('token') || localStorage.getItem('adminToken');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle errors globally
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (axios.isCancel(error)) {
      // Request was cancelled, don't treat as error
      return Promise.reject({ cancelled: true });
    }
    return Promise.reject(error);
  }
);

// Helper to create cancellable requests
export const createCancelToken = () => axios.CancelToken.source();

// Products API
export const productsAPI = {
  getAll: (params = {}) => api.get('/products/', { params }),
  getBySlug: (slug) => api.get(`/products/${slug}`),
  getById: (id) => api.get(`/products/by-id/${id}`),
  getByIds: (ids) => api.post('/products/by-ids', ids),
  getCount: (params = {}) => api.get('/products/count', { params }),
  create: (data) => api.post('/products/', data),
  update: (id, data) => api.put(`/products/${id}`, data),
  delete: (id) => api.delete(`/products/${id}`),
  bulkDelete: (ids) => api.post('/products/bulk-delete', ids),
  getDynamicFilterCounts: (params = {}) => api.get('/products/filter-counts/dynamic', { params }),
};

// Brands API
export const brandsAPI = {
  getAll: () => api.get('/brands/'),
  getById: (id) => api.get(`/brands/${id}`),
  create: (data) => api.post('/brands/', data),
  update: (id, data) => api.put(`/brands/${id}`, data),
  delete: (id) => api.delete(`/brands/${id}`),
  bulkDelete: (ids) => api.post('/brands/bulk-delete', ids),
};

// Categories API
export const categoriesAPI = {
  getAll: () => api.get('/categories/'),
  getById: (id) => api.get(`/categories/${id}`),
  create: (data) => api.post('/categories/', data),
  update: (id, data) => api.put(`/categories/${id}`, data),
  delete: (id) => api.delete(`/categories/${id}`),
  bulkDelete: (ids) => api.post('/categories/bulk-delete', ids),
};

// Orders API
export const ordersAPI = {
  getAll: (params = {}) => api.get('/orders/admin/all', { params }),
  getById: (id) => api.get(`/orders/${id}`),
  create: (data) => api.post('/orders/', data),
  updateStatus: (id, status, trackingNumber) => 
    api.put(`/orders/${id}/status`, null, { params: { status, tracking_number: trackingNumber } }),
  getMyOrders: () => api.get('/users/orders'),
};

// Blog API
export const blogAPI = {
  getAll: (params = {}) => api.get('/blog/', { params }),
  getBySlug: (slug) => api.get(`/blog/${slug}`),
  create: (data) => api.post('/blog/', data),
  update: (id, data) => api.put(`/blog/${id}`, data),
  delete: (id) => api.delete(`/blog/${id}`),
};

// Auth API
export const authAPI = {
  login: (email, password) => api.post('/auth/login', new URLSearchParams({ username: email, password })),
  register: (data) => api.post('/auth/register', data),
  getMe: () => api.get('/auth/me'),
};

// User API
export const userAPI = {
  getProfile: () => api.get('/users/profile'),
  updateProfile: (data) => api.put('/users/profile', data),
  
  // Addresses
  getAddresses: () => api.get('/users/addresses'),
  createAddress: (data) => api.post('/users/addresses', data),
  updateAddress: (id, data) => api.put(`/users/addresses/${id}`, data),
  deleteAddress: (id) => api.delete(`/users/addresses/${id}`),
  setDefaultAddress: (id) => api.post(`/users/addresses/${id}/set-default`),
  
  // Loyalty
  getLoyalty: () => api.get('/users/loyalty'),
  redeemPoints: (data) => api.post('/users/loyalty/redeem', null, { params: { points_to_redeem: data.points } }),
  
  // Orders
  getMyOrders: () => api.get('/users/orders'),
};

// Newsletter API
export const newsletterAPI = {
  subscribe: (email) => api.post('/newsletter/subscribe', { email }),
  unsubscribe: (email) => api.post('/newsletter/unsubscribe', { email }),
};

// Promo Codes API
export const promoCodesAPI = {
  getAll: (params = {}) => api.get('/promocodes/', { params }),
  validate: (code, cartTotal) => api.get(`/promocodes/${code}/validate`, { params: { cart_total: cartTotal } }),
  create: (data) => api.post('/promocodes/', data),
  update: (id, data) => api.put(`/promocodes/${id}`, data),
  toggle: (id) => api.put(`/promocodes/${id}/toggle`),
  delete: (id) => api.delete(`/promocodes/${id}`),
  use: (code) => api.post(`/promocodes/${code}/use`),
};

// Bulk operations API
export const bulkAPI = {
  uploadExcel: (formData) => api.post('/bulk/upload-excel', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
};

// Image Upload API
export const uploadAPI = {
  uploadImage: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/uploads/image', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  uploadMultiple: (files) => {
    const formData = new FormData();
    files.forEach(file => formData.append('files', file));
    return api.post('/uploads/images', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  deleteImage: (filename) => api.delete(`/uploads/${filename}`),
  listImages: () => api.get('/uploads/list'),
};

// Cart API
export const cartAPI = {
  get: (userId) => api.get(`/cart/${userId}`),
  addItem: (userId, productId, quantity = 1) => api.post(`/cart/${userId}/add`, { product_id: productId, quantity }),
  updateQuantity: (userId, productId, quantity) => api.put(`/cart/${userId}/update/${productId}`, { quantity }),
  removeItem: (userId, productId) => api.delete(`/cart/${userId}/remove/${productId}`),
  clear: (userId) => api.delete(`/cart/${userId}/clear`),
};

// Wishlist API
export const wishlistAPI = {
  get: (userId) => api.get(`/wishlist/${userId}`),
  getProducts: (userId) => api.get(`/wishlist/${userId}/products`),
  add: (userId, productId) => api.post(`/wishlist/${userId}/add/${productId}`),
  remove: (userId, productId) => api.delete(`/wishlist/${userId}/remove/${productId}`),
  check: (userId, productId) => api.get(`/wishlist/${userId}/check/${productId}`),
};

// Homepage Settings API
export const homepageAPI = {
  getSettings: () => api.get('/homepage/settings'),
  updateSettings: (settings) => api.put('/homepage/settings', settings),
  getSliders: () => api.get('/homepage/sliders'),
  addSlider: (slider) => api.post('/homepage/sliders', slider),
  updateSlider: (index, slider) => api.put(`/homepage/sliders/${index}`, slider),
  deleteSlider: (index) => api.delete(`/homepage/sliders/${index}`),
  getCategories: () => api.get('/homepage/categories'),
  updateCategories: (data) => api.put('/homepage/categories', null, { params: data }),
  updateCategoryDisplay: (categoryId, display) => api.put(`/homepage/categories/${categoryId}`, display),
};

// Email API
export const emailAPI = {
  requestPasswordReset: (email) => api.post('/email/password-reset/request', { email }),
  confirmPasswordReset: (token, newPassword) => api.post('/email/password-reset/confirm', { token, new_password: newPassword }),
  sendOrderConfirmation: (orderId, email) => api.post('/email/order-confirmation', null, { params: { order_id: orderId, email } }),
};

// Settings API
export const settingsAPI = {
  get: () => api.get('/settings/'),
  update: (data) => api.put('/settings/', data),
};

export default api;
